/**
 * Custom error classes for Filestack third-party service operations
 * These provide consistent error messages for Sentry grouping
 * while preserving context data for debugging
 */

import { CustomError } from '../../utility/error/CustomError'
import { PuError } from '../../utility/error/PuError'

/**
 * Base error class for Filestack operations
 * Provides consistent error messages for Sentry grouping while preserving context data for debugging
 */
export class FilestackError extends CustomError implements PuError {
  readonly statusCode: number = ERROR_CODE_FILESTACK

  public readonly operation: string
  public readonly filename?: string
  public readonly fileSize?: number
  public readonly fileMetadata?: any
  public readonly originalError?: any

  constructor(
    operation: string,
    filename?: string,
    fileMetadata?: any,
    originalError?: any,
  ) {
    // Generate consistent message for Sentry grouping (no variable data)
    const message = `Filestack ${operation} failed`

    super({
      name: 'FilestackError',
      message,
      cause: originalError,
    })

    // Store context data (variable data that shouldn't be in the error message)
    this.operation = operation
    this.filename = filename
    this.fileSize = fileMetadata?.size
    this.fileMetadata = fileMetadata
    this.originalError = originalError
  }

  /**
   * Get context data for Sentry or logging
   * This includes all the variable data that shouldn't be in the error message
   */
  getContext(): Record<string, unknown> {
    return {
      operation: this.operation,
      filename: this.filename,
      fileSize: this.fileSize,
      fileSizeFormatted: this.fileSize
        ? `${Math.round(this.fileSize / 1024)}KB`
        : undefined,
      fileType: this.fileMetadata?.mimetype,
      fileSource: this.fileMetadata?.source,
      fileMetadata: this.fileMetadata
        ? JSON.stringify(this.fileMetadata)
        : undefined,
      originalErrorMessage: this.originalError?.message,
      originalErrorType:
        this.originalError?.name || this.originalError?.constructor?.name,
      stack: this.stack || 'No stack trace available',
    }
  }
}

/**
 * Specific error for file validation failures
 * Groups all validation failures together regardless of specific validation type
 */
export class FilestackValidationError extends FilestackError {
  public readonly validationType: string
  public readonly expectedValue?: number
  public readonly actualValue?: number

  constructor(
    validationType: string,
    filename: string,
    expectedValue?: number,
    actualValue?: number,
    fileMetadata?: any,
  ) {
    super('file validation', filename, fileMetadata)

    // Override name for specific error type
    Object.defineProperty(this, 'name', {
      value: 'FilestackValidationError',
      enumerable: true,
    })

    this.validationType = validationType
    this.expectedValue = expectedValue
    this.actualValue = actualValue
  }

  /**
   * Get context data including validation-specific details
   */
  override getContext(): Record<string, unknown> {
    const baseContext = super.getContext()
    return {
      ...baseContext,
      validationType: this.validationType,
      expectedValue: this.expectedValue,
      actualValue: this.actualValue,
      validationMessage: this.getValidationMessage(),
    }
  }

  private getValidationMessage(): string {
    switch (this.validationType) {
      case 'file-too-large':
        return `File size ${this.actualValue}KB exceeds maximum ${this.expectedValue}KB`
      case 'file-too-small':
        return `Image dimensions too small: expected minimum ${this.expectedValue}px`
      case 'no-original-file':
        return 'Original file not available from source (common with social media)'
      case 'image-load-failed':
        return 'Failed to load image for dimension validation'
      default:
        return `Validation failed: ${this.validationType}`
    }
  }
}

export const ERROR_CODE_FILESTACK = 503
